import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { findNavTrail } from '../config/navigation'

/**
 * 按当前路由从站点结构（src/config/navigation.js）算出面包屑。
 *
 * 页面里不要再写死 breadcrumb={[{ label: 'xxx' }, ...]} —— 那样导航结构一调整
 * 就得记得挨个同步，而这件事已经漏过一次（2026-08-01「我的」并入账号菜单后，
 * FinancePage 与 IssuesPage 的面包屑仍写着「客户中心 / …」）。
 *
 * @param {Array<{label: string, path?: string}>} suffix
 *        当前页之下还有一层时追加，例如报名详情的单号、财务管理的 ?tab= 名称。
 *        调用方请用 useMemo 稳定这个数组，或直接传字面量（下面按内容比对，不怕重建）。
 * @returns {Array<{label: string, path?: string}>} 交给 PageHeader 的 breadcrumb
 */
export function useBreadcrumb(suffix = []) {
  const { pathname } = useLocation()
  // 依赖用序列化后的 suffix：调用方多半直接传字面量数组，每次渲染都是新引用，
  // 按引用比对等于没有 memo
  const suffixKey = JSON.stringify(suffix)

  return useMemo(() => {
    const trail = findNavTrail(pathname) ?? []
    const crumbs = trail.map((node, index) => ({
      label: node.breadcrumbLabel || node.label,
      // 最后一项是当前所在页，不做成链接；祖先里只有带 path 的才可点
      // （「集运板块」「我的」这类分组没有 path，渲染成纯文本）
      path: index < trail.length - 1 ? node.path : undefined,
    }))
    const extra = JSON.parse(suffixKey)
    if (extra.length === 0) {
      return crumbs
    }
    // 有后缀时，原本的最后一项不再是当前页，把它的链接补回来
    const linked = crumbs.map((crumb, index) =>
      index === crumbs.length - 1 ? { ...crumb, path: trail[index].path } : crumb
    )
    return [...linked, ...extra]
  }, [pathname, suffixKey])
}

export default useBreadcrumb
